import { Button, Collapse, Popconfirm, Tooltip } from "antd";
import {
  ClearOutlined,
  DownloadOutlined,
  LockOutlined,
  MenuFoldOutlined,
  ReloadOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { datasetByKey } from "@/warRoom/datasets/registry";
import { formByType } from "@/warRoom/graph/chartForms";
import {
  useAnyLoading,
  useGraphStore,
  useSelectedWidget,
} from "@/warRoom/store/useGraphStore";

import ChartPanel from "./ChartPanel";
import DataPanel from "./DataPanel";
import InspectorPanel from "./InspectorPanel";

type StepKey = "data" | "chart" | "axis";

/**
 * 工具箱（左側）。一條路：① 選資料 → ② 把圖表拖到畫布 → ③ 調選中那張圖的軸。
 * 後面的步驟在前一步完成前是鎖著的，完成一步就把焦點帶到下一步 —— 那是「引導」。
 * 第 ③ 步跟著畫布上的選取跑：點哪一張圖，這一段就是那一張的設定。
 * 同一時間只開一段；展開的那一段再點一下就收起來。
 */
export default function Toolbox({
  exporting = false,
  onExport,
  onCollapse,
}: {
  exporting?: boolean;
  onExport?: () => void;
  onCollapse?: () => void;
}) {
  const { t } = useTranslation();
  const activeDatasetKey = useGraphStore((s) => s.activeDatasetKey);
  const widgetCount = useGraphStore((s) => s.widgets.length);
  const selected = useSelectedWidget();
  const loading = useAnyLoading();
  const reload = useGraphStore((s) => s.reload);
  const reset = useGraphStore((s) => s.reset);
  const clearCanvas = useGraphStore((s) => s.clearCanvas);

  const activeDataset = activeDatasetKey
    ? datasetByKey(activeDatasetKey)
    : undefined;
  const spec = selected?.spec;
  const selectedDataset = spec?.datasetKey
    ? datasetByKey(spec.datasetKey)
    : undefined;
  const form = spec?.chartType ? formByType(spec.chartType) : undefined;
  const xLabel = selectedDataset?.fields.find(
    (f) => f.key === spec?.xField,
  )?.label;
  const yLabel = spec?.yField
    ? selectedDataset?.fields.find((f) => f.key === spec.yField)?.label
    : t("warRoom.common.count");

  // 重新掛載（換頁回來）時開在「現在該做的那一步」，不是永遠回到第一步
  const [openKey, setOpenKey] = useState<StepKey | null>(() => {
    const s = useGraphStore.getState();
    if (s.selectedId) return "axis";
    return s.activeDatasetKey ? "chart" : "data";
  });

  // 鎖住的步驟不能留在展開狀態（例如按了「全部重來」）
  const unlocked = (k: StepKey) =>
    k === "data" || (k === "chart" ? !!activeDataset : !!selected);

  // 完成一步就把焦點帶到下一步：選了資料 → ②；畫布上多了一張圖或換選了一張 → ③。
  const prev = useRef({
    dataset: activeDatasetKey,
    selectedId: selected?.id ?? null,
  });
  useEffect(() => {
    const last = prev.current;
    const selectedId = selected?.id ?? null;
    prev.current = { dataset: activeDatasetKey, selectedId };

    const next: StepKey | null =
      selectedId && selectedId !== last.selectedId
        ? "axis" // 剛拖進畫布／剛點選一張圖 → 直接可以調它的軸
        : activeDatasetKey !== last.dataset
          ? activeDatasetKey
            ? "chart"
            : "data" // 全部重來：回到第一步
          : null;

    // 沒有哪一步剛完成就維持現狀；那一段被鎖住時（例如剛把選中的圖刪掉）
    // 退回還走得到的最後一步，而不是整個工具箱收起來。
    setOpenKey((key) => {
      const k = next ?? key;
      if (k && unlocked(k)) return k;
      return selectedId ? "axis" : activeDatasetKey ? "chart" : "data";
    });
    // unlocked 讀的是這一輪 render 的 activeDataset / selected，不用進依賴陣列
  }, [activeDatasetKey, selected?.id]);

  // 選好的步驟用「號碼上色」表示，不再多一個打勾圖示 —— 同一件事講兩次
  const header = (
    n: string,
    title: string,
    value?: string,
    locked?: boolean,
  ) => (
    <div className="step__header">
      <span className={`step__no${value ? " is-done" : ""}`}>{n}</span>
      <span className="step__title">{title}</span>
      {locked ? (
        <LockOutlined className="step__lock" />
      ) : value ? (
        <span className="step__value">{value}</span>
      ) : null}
    </div>
  );

  const items = [
    {
      key: "data",
      // 收起來時也要看得出這份資料是給「新圖」用的，不是整塊畫布的資料
      label: header(
        "1",
        t("warRoom.toolbox.steps.data"),
        activeDataset
          ? t("warRoom.toolbox.steps.dataValue", { name: activeDataset.label })
          : undefined,
      ),
      children: <DataPanel />,
    },
    {
      key: "chart",
      label: header(
        "2",
        t("warRoom.toolbox.steps.chart"),
        widgetCount > 0
          ? t("warRoom.toolbox.steps.chartValue", { count: widgetCount })
          : undefined,
        !activeDataset,
      ),
      children: <ChartPanel />,
      collapsible: (activeDataset ? undefined : "disabled") as
        | "disabled"
        | undefined,
    },
    {
      key: "axis",
      label: header(
        "3",
        t("warRoom.toolbox.steps.axis"),
        selected && form && xLabel ? `${xLabel} × ${yLabel}` : undefined,
        !selected,
      ),
      children: <InspectorPanel />,
      collapsible: (selected ? undefined : "disabled") as
        | "disabled"
        | undefined,
    },
  ];

  return (
    <div className="toolbox">
      <div className="toolbox__header">
        <span className="toolbox__heading">
          {onCollapse ? (
            <Tooltip title={t("warRoom.toolbox.collapse")}>
              <Button
                size="small"
                type="text"
                aria-label={t("warRoom.toolbox.collapse")}
                icon={<MenuFoldOutlined />}
                onClick={onCollapse}
              />
            </Tooltip>
          ) : null}
          <span className="toolbox__title">{t("warRoom.toolbox.title")}</span>
        </span>
        <span className="toolbox__actions">
          {onExport ? (
            <Tooltip title={t("warRoom.toolbox.exportTooltip")}>
              <Button
                size="small"
                type="text"
                aria-label={t("warRoom.toolbox.export")}
                icon={<DownloadOutlined />}
                loading={exporting}
                disabled={widgetCount === 0}
                onClick={onExport}
              />
            </Tooltip>
          ) : null}
          {widgetCount > 0 ? (
            <Popconfirm
              title={t("warRoom.toolbox.clearCanvas")}
              description={t("warRoom.toolbox.clearConfirm", {
                count: widgetCount,
              })}
              okText={t("warRoom.toolbox.clearOk")}
              cancelText={t("warRoom.toolbox.clearCancel")}
              onConfirm={clearCanvas}
            >
              <Tooltip title={t("warRoom.toolbox.clearCanvas")}>
                <Button
                  size="small"
                  type="text"
                  aria-label={t("warRoom.toolbox.clearCanvas")}
                  icon={<ClearOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
          <Tooltip title={t("warRoom.toolbox.reload")}>
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void reload()}
              disabled={!activeDataset && widgetCount === 0}
            />
          </Tooltip>
          <Tooltip title={t("warRoom.toolbox.reset")}>
            <Button
              size="small"
              type="text"
              icon={<UndoOutlined />}
              onClick={reset}
            />
          </Tooltip>
        </span>
      </div>

      <Collapse
        accordion
        ghost
        className="steps"
        items={items}
        activeKey={openKey ? [openKey] : []}
        onChange={(keys) => {
          const [next] = Array.isArray(keys) ? keys : [keys];
          setOpenKey((next as StepKey) ?? null);
        }}
        expandIconPosition="end"
      />
    </div>
  );
}
